import React from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Tooltip,
  Alert as MuiAlert,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ResponsiveNavBar } from "../../common/ResponsiveNavBar";
import {
  isProdApi,
  samplePreviewAvailable,
  STAGING_DATA_NOTE,
  STAGING_NOTE_ACK_KEY,
} from "./TroubleshootingUtils/apiEnvironment";
import { useNavOffset } from "../../common/navLayout";
import { Dispatcher } from "../WireGuardTunnels/wireGuardTunnelsInterfaces";
import { useTroubleshootingStore } from "./troubleshootingStore/troubleshootingStore";
import {
  TroubleshootingPageTabs,
  visibleTroubleshootingTab,
} from "./TroubleshootingPageTabs";
import { TroubleshootingScopeBar } from "./TroubleshootingScopeBar";
import { EmptyCard, Hint, TsButton } from "./TroubleshootingUi";
import { DiagnosisTab } from "./tabs/DiagnosisTab";
import { ConnectionsTab } from "./tabs/ConnectionsTab";
import { WireGuardTab } from "./tabs/WireGuardTab";
import { SmtpCamerasTab } from "./tabs/SmtpCamerasTab";
import { FilenameRuleSummary } from "./tabs/SmtpFilenameDistribution";
import { AlarmReceiversTab, ReceiverTestState } from "./tabs/AlarmReceiversTab";
import { FisheyeDewarpTab } from "./tabs/FisheyeDewarpTab";
import { useIsStaffUser } from "../../utils/staffUser";
import {
  useReloadReceiverTests,
  useTroubleshootingData,
} from "./TroubleshootingUtils/useTroubleshootingData";
import {
  UNSCOPED_TABS,
  findingsInScope,
  receiverIdsInScope,
  scopedSitesFor,
  useScopeLabel,
  useSelectionScope,
  useServerName,
} from "./TroubleshootingUtils/useScope";
import {
  RUN_ALL_COOLDOWN_SECONDS,
  useCooldown,
} from "./TroubleshootingUtils/useCooldown";
import {
  buildDiagnosticReport,
  copyToClipboard,
} from "./TroubleshootingUtils/buildDiagnosticReport";
import {
  clearSiteLookupCache,
  probeReceiver,
  sendReceiverTest,
} from "./TroubleshootingUtils/troubleshootingApi";
import { formatRelative } from "./TroubleshootingUtils/formatters";
import { useAllClear } from "./TroubleshootingUtils/allClear";

/**
 * A listener check's result in the words the receiver table uses, not the
 * port scanner's ("filtered" read as a fault; QA AR-02).
 */
const PROBE_RESULT_WORDS: Record<string, string> = {
  closed: "refused",
  filtered: "timed out",
  error: "could not be checked",
};

/**
 * Troubleshooting page (redesign).
 *
 * One place for a customer to answer "why am I not getting alarms" without a
 * ticket: a ranked list of what is wrong and whose side the fix is on, then
 * the raw surfaces behind it — connections, WireGuard peer health and the
 * gateway-side debugger, SMTP delivery and the filename rule, and alarm
 * receivers. Filters sit on a white bar; the tabs and their contents sit in a
 * rounded panel on the grey page.
 */
export const Troubleshooting = ({
  navBarCollapsed,
  setNavBarCollapsed,
}: {
  navBarCollapsed: boolean;
  setNavBarCollapsed: Dispatcher<boolean>;
}) => {
  const {
    tab: storedTab,
    setTab,
    lastCheckedAt,
    requestRefresh,
    toast,
    setToast,
    findings,
    sites,
    sampleData,
    receivers,
    alertsReceived,
    cameraLastAlarms,
    siteCameraTest,
    siteChanges,
    siteChangesCompare,
    cameraLiveness,
    groupId,
    parentAccounts,
    accountsLoading,
    clearLoadedData,
    scope,
    siteId,
  } = useTroubleshootingStore();
  // Staff-only tabs (Fisheye dewarp) fall back to Diagnosis for everyone else.
  const staff = useIsStaffUser();
  const tab = visibleTroubleshootingTab(storedTab, staff);
  // The Debugger still lists every tunnel; the filters stay visible so the
  // rest of the page can be narrowed without leaving the tab.
  const showScopeBar = !UNSCOPED_TABS.includes(tab);
  const [stagingNoteOpen, setStagingNoteOpen] = React.useState(
    () => !isProdApi() && localStorage.getItem(STAGING_NOTE_ACK_KEY) !== "1"
  );
  const getServerName = useServerName();
  const scopeLabel = useScopeLabel();
  const allClear = useAllClear();
  const selection = useSelectionScope();
  const setSampleData = useTroubleshootingStore((st) => st.setSampleData);
  // Production builds and production mode hide the sample switch; never
  // carry sample data into them.
  React.useEffect(() => {
    if (!samplePreviewAvailable() && sampleData) setSampleData(false);
  }, [sampleData, setSampleData]);
  const runAllCooldown = useCooldown();
  const [smtpSummary, setSmtpSummary] =
    React.useState<FilenameRuleSummary | null>(null);
  const [receiverTests, setReceiverTests] = React.useState<ReceiverTestState>(
    {}
  );
  // Re-render every 15 s so "Checked N s ago" stays honest without a fetch.
  const [, setTick] = React.useState(0);

  React.useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 15_000);
    return () => window.clearInterval(id);
  }, []);

  // Loaded data is dropped on the way out so the next visit checks afresh;
  // the tab and each tab's group and site are kept (and persisted).
  React.useEffect(() => () => clearLoadedData(), [clearLoadedData]);

  useTroubleshootingData(getServerName, smtpSummary);
  const reloadReceiverTests = useReloadReceiverTests();

  const copyText = async (
    text: string,
    okMessage = "Copied. Paste it into your support ticket."
  ) => {
    const ok = await copyToClipboard(text);
    setToast(ok ? okMessage : "Could not copy to the clipboard.");
  };

  const copyReport = () =>
    copyText(
      buildDiagnosticReport({
        scopeLabel,
        // The findings Diagnosis shows for this scope, not every one loaded
        // (QA DG-35).
        findings: findingsInScope(
          findings,
          scope,
          scopedSitesFor(scope, groupId, siteId, sites).map((s) => s.id)
        ),
        tunnels: selection.tunnels,
        sites,
        getServerName,
        lastCheckedAt,
        sampleData,
        alertsReceived:
          alertsReceived?.status === "ok" ? alertsReceived.data : null,
        cameraLastAlarms:
          cameraLastAlarms?.status === "ok" ? cameraLastAlarms.data : null,
        siteCameraTest:
          scope === "site" && siteCameraTest?.siteId === siteId
            ? siteCameraTest
            : null,
        siteChanges: siteChanges?.status === "ok" ? siteChanges.data : null,
        siteChangesCompare:
          scope === "site" &&
          siteChangesCompare?.siteId === siteId &&
          siteChangesCompare.result?.status === "ok"
            ? siteChangesCompare.result.data
            : null,
        cameraLiveness: scope === "site" ? cameraLiveness : null,
        allClear,
      }),
      "Diagnostic report copied. Paste it into your support ticket."
    );

  const testReceiver = async (receiverId: string) => {
    const receiver =
      receivers?.status === "ok"
        ? receivers.data.find((r) => r.id === receiverId)
        : null;
    if (!receiver) return;
    setReceiverTests((prev) => ({ ...prev, [receiverId]: "running" }));
    const result = await sendReceiverTest(receiver, sampleData);
    if (result.status === "ok") {
      setReceiverTests((prev) => ({ ...prev, [receiverId]: result.data }));
      // Show it in the test history now, not at the next minute refresh.
      reloadReceiverTests();
    } else {
      setReceiverTests((prev) => {
        const next = { ...prev };
        delete next[receiverId];
        return next;
      });
      setToast(
        result.status === "unavailable"
          ? `Test alarms need ${result.endpoint}; not available in this environment yet.`
          : result.message
      );
    }
  };

  const [probing, setProbing] = React.useState<Record<string, boolean>>({});
  const probeReceiverListener = async (receiverId: string) => {
    const receiver =
      receivers?.status === "ok"
        ? receivers.data.find((r) => r.id === receiverId)
        : null;
    if (!receiver) return;
    setProbing((prev) => ({ ...prev, [receiverId]: true }));
    const result = await probeReceiver(receiver, sampleData);
    setProbing((prev) => ({ ...prev, [receiverId]: false }));
    if (result.status === "ok") {
      setToast(
        result.data.result === "open"
          ? `${receiver.host}:${receiver.port} is listening${
              result.data.ms != null ? ` (${result.data.ms} ms)` : ""
            }.`
          : `${receiver.host}:${receiver.port}: ${
              PROBE_RESULT_WORDS[result.data.result] ?? result.data.result
            }.`
      );
      requestRefresh();
    } else {
      setToast(
        result.status === "unavailable"
          ? `Listener checks need ${result.endpoint}; not available in this environment yet.`
          : result.message
      );
    }
  };

  // Only the receivers serving the sites in scope: a site's operator asked
  // for a test of *their* path, not a page to every monitoring station.
  const testAll = () => {
    if (receivers?.status !== "ok" || receivers.data.length === 0) {
      setToast("No alarm receivers are loaded to test.");
      return;
    }
    const ids = receiverIdsInScope(
      receivers.data,
      scope,
      scopedSitesFor(scope, groupId, siteId, sites).map((s) => s.id)
    );
    if (ids.length === 0) {
      setToast("No alarm receiver serves the sites in scope.");
      return;
    }
    ids.forEach((id) => void testReceiver(id));
  };

  // Sidebar on desktop, top bar on small screens (redesign mobile shell).
  const nav = useNavOffset(navBarCollapsed);
  const leftOffset = nav.left;
  const [filtersCollapsed, setFiltersCollapsed] = React.useState(false);

  const runAllChecks = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 0.5,
      }}
    >
      <TsButton
        size="sm"
        disabled={runAllCooldown.remaining > 0}
        onClick={() => {
          // A manual run re-reads the site list; the minute timer
          // reuses it (SITE_LOOKUP_TTL_MS). It is reads only, a
          // fixed handful of requests whatever the group's size,
          // but repeated clicks still multiply them.
          clearSiteLookupCache();
          requestRefresh();
          runAllCooldown.start(RUN_ALL_COOLDOWN_SECONDS);
        }}
        startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
        aria-label="Run all checks"
        title="Run all checks"
        data-testid="run-all-checks"
      >
        {runAllCooldown.remaining > 0
          ? `Checked · run again in ${runAllCooldown.remaining} s`
          : "Run all checks"}
      </TsButton>
      <Hint sx={{ display: "block", textAlign: "right" }}>
        {lastCheckedAt
          ? `Checked ${formatRelative(
              new Date(lastCheckedAt).toISOString()
            )}, refreshes every 60 s`
          : "Running checks…"}
      </Hint>
    </Box>
  );

  const filtersToggle = (
    <Tooltip
      title={filtersCollapsed ? "Expand filters" : "Collapse filters"}
      placement="top"
    >
      <button
        type="button"
        aria-label={filtersCollapsed ? "Expand filters" : "Collapse filters"}
        aria-expanded={!filtersCollapsed}
        aria-controls="troubleshooting-filters-fields"
        onClick={() => setFiltersCollapsed((current) => !current)}
        className="inline-flex items-center justify-center text-actuate-blue"
      >
        {filtersCollapsed ? (
          <ExpandMoreIcon sx={{ fontSize: 22 }} />
        ) : (
          <ExpandLessIcon sx={{ fontSize: 22 }} />
        )}
      </button>
    </Tooltip>
  );

  const filterActions = (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
      {runAllChecks}
      <Box sx={{ pt: "2px" }}>{filtersToggle}</Box>
    </Box>
  );

  return (
    <div>
      <div
        className="absolute inset-0 flex flex-col bg-actuate-grey"
        style={{ top: nav.top }}
      >
        <div
          className="w-full shrink-0 border-b bg-white"
          style={{ borderColor: "#d1d5db" }}
        >
          <Box
            sx={{
              ml: `${leftOffset}px`,
              px: 2,
              py: filtersCollapsed ? 0.5 : 2,
            }}
          >
            {filtersCollapsed ? (
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                {filtersToggle}
              </Box>
            ) : showScopeBar ? (
              <Box id="troubleshooting-filters-fields">
                <TroubleshootingScopeBar actions={filterActions} />
              </Box>
            ) : (
              <Box
                id="troubleshooting-filters-fields"
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                {filterActions}
              </Box>
            )}
          </Box>
        </div>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            ml: `${leftOffset}px`,
            px: 2,
            pt: 2,
            pb: 5,
          }}
        >
          {sampleData && (
            <Box
              role="status"
              data-testid="sample-preview-banner"
              sx={{
                mb: 2,
                px: 2,
                py: 1.25,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                border: "1px solid #F59E0B",
                borderRadius: "6px",
                backgroundColor: "#FFFBEB",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 220,
                  fontSize: 14,
                  fontFamily: "Mulish",
                }}
              >
                <b>Sample preview is on.</b> Cards marked SAMPLE are made-up
                examples mixed in with your real results; they are not about
                your sites.
              </Box>
              <TsButton
                size="sm"
                variant="outline"
                onClick={() => setSampleData(false)}
                data-testid="sample-preview-off"
              >
                Turn off
              </TsButton>
            </Box>
          )}
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              boxShadow:
                "0px 1px 3px rgba(0, 25, 67, 0.12), 0px 1px 2px rgba(0, 25, 67, 0.08)",
              overflow: "hidden",
            }}
          >
            <TroubleshootingPageTabs activeTab={tab} setActiveTab={setTab} />
            <Box sx={{ p: { xs: 1.25, md: 2.5 } }} role="tabpanel">
              {/* Diagnosis requires a group before it checks anything: every
                account's findings at once was more than a customer could read.
                The other tabs work across all groups — a tunnel list, the
                receiver table, the connectivity tools — and narrow when a group
                or site is chosen. One account is chosen for them, so this only
                shows when there is a real choice — and never when the group
                list itself failed to load, or this would hide the error the
                tab is there to report. */}
              {tab === "diagnosis" &&
              groupId == null &&
              parentAccounts.length > 0 ? (
                <EmptyCard>
                  {accountsLoading
                    ? "Loading your groups…"
                    : "Choose a group above to check its sites. You can then narrow to one site."}
                </EmptyCard>
              ) : (
                <>
                  {tab === "diagnosis" && (
                    <DiagnosisTab onCopyReport={copyReport} />
                  )}
                  {tab === "connections" && (
                    <ConnectionsTab onCopy={(text) => copyText(text)} />
                  )}
                  {tab === "wireguard" && <WireGuardTab onCopy={copyReport} />}
                  {tab === "smtp-cameras" && (
                    <SmtpCamerasTab
                      onCopy={(text) => copyText(text)}
                      onFilenameSummary={setSmtpSummary}
                    />
                  )}
                  {tab === "alarm-receivers" && (
                    <AlarmReceiversTab
                      tests={receiverTests}
                      onTest={testReceiver}
                      onTestAll={testAll}
                      onProbe={probeReceiverListener}
                      probing={probing}
                    />
                  )}
                  {tab === "fisheye-dewarp" && <FisheyeDewarpTab />}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </div>
      <Dialog
        open={stagingNoteOpen}
        disableEscapeKeyDown
        aria-labelledby="staging-data-note-title"
        data-testid="staging-data-note"
        onClose={() => {
          // Stays up until Acknowledge. Backdrop and Escape do not dismiss it.
        }}
      >
        <DialogTitle
          id="staging-data-note-title"
          sx={{ fontFamily: "Mulish", fontWeight: 800, color: "#001943" }}
        >
          Not production data
        </DialogTitle>
        <DialogContent>
          <Box sx={{ fontFamily: "Mulish", fontSize: 14, color: "#4c5257" }}>
            {STAGING_DATA_NOTE}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <TsButton
            onClick={() => {
              localStorage.setItem(STAGING_NOTE_ACK_KEY, "1");
              setStagingNoteOpen(false);
            }}
          >
            Acknowledge
          </TsButton>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity="info"
          sx={{ fontFamily: "Mulish", backgroundColor: "#001943" }}
          onClose={() => setToast("")}
        >
          {toast}
        </MuiAlert>
      </Snackbar>
      <div className="justify-start">
        <ResponsiveNavBar
          navBarCollapsed={navBarCollapsed}
          setNavBarCollapsed={setNavBarCollapsed}
        />
      </div>
    </div>
  );
};
